import { APIError } from "@certn/api";
import { useSubmit } from "@certn/api/src/useSubmit";
import { showToast } from "@certn/cascadia";
import {
  Divider,
  Grid,
  PhoneNumberInputField,
  Text,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { regexTypes } from "@certn/utils";
import { Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { array, object, string } from "yup";

import { CreateBillingProfilePageLayout } from "./CreateBillingProfileProfileLayout";
import { useCreateBillingProfileContext } from "../CreateBillingProfileContext";

export function BillingProfileContactInformationStep() {
  const { t } = useTranslation();
  const { billingProfile } = useCreateBillingProfileContext();
  const navigate = useNavigate();

  const { submit } = useSubmit(
    "/api/internal/client-portal/billing-profiles/create/",
    {
      throwErrors: true,
      onSuccess: () => {
        navigate("/billing/billing-profiles");
      },
      onFailure: (error) => {
        if (
          error instanceof APIError &&
          error.status === 403 &&
          error.body.code === "BILLING_PROFILE_CREATION_BLOCKED_COLLECTIONS"
        ) {
          showToast(
            t(
              "New billing profiles can't be created while a billing profile on this account is paused for collections. Pay the overdue balance to restore access."
            ),
            { type: "error" }
          );
        }
      },
    }
  );

  const schema = object({
    contact_first_name: string().required().label(t("Given name")),
    contact_last_name: string().required().label(t("Family name")),
    contact_email: string().email().required().label(t("Email")),
    contact_phone: string().nullable().label(t("Phone number")),
    cc_emails: array()
      .of(string())
      .test(
        "invalidEmail",
        t("Please check all the entries are valid emails"),
        (emails) => {
          return emails?.every((item) => {
            return string()
              .email()
              .matches(regexTypes.checkMultipleEmails)
              .isValidSync(item);
          });
        }
      ),
  });

  return (
    <Formik
      initialValues={billingProfile}
      onSubmit={(values) => submit(values)}
      validationSchema={schema}
    >
      {({ handleSubmit, isSubmitting, dirty, setFieldValue }) => {
        return (
          <CreateBillingProfilePageLayout
            onComplete={handleSubmit}
            submitting={isSubmitting}
            formIsDirty={dirty}
          >
            <Grid form>
              <Text style="form/section-heading">
                <Trans>Primary contact details</Trans>
              </Text>
              <TextInputFormikField
                name="contact_first_name"
                label={t("Given name")}
                isRequired
                testID="billing-contact-information-first-name"
              />
              <TextInputFormikField
                name="contact_last_name"
                label={t("Family name")}
                isRequired
                testID="billing-contact-information-last-name"
              />
              <TextInputFormikField
                type="email"
                name="contact_email"
                label={t("Email")}
                isRequired
                testID="billing-contact-information-email"
              />
              <PhoneNumberInputField
                autoComplete="tel"
                label={t("Phone number")}
                name="contact_phone"
                testID="billing-contact-information-phone-number"
                type="string"
              />
              <Divider />
              <Text style="form/section-heading">
                <Trans>Additional invoice recipients</Trans>
              </Text>
              <TextInputFormikField
                name="cc_emails"
                label={`${t("Email(s)")}`}
                testID="billing-contact-information-optional-emails"
                description={t(
                  "To send multiple recipients, separate each email with a comma."
                )}
                onChange={(value) => {
                  setFieldValue("cc_emails", value.split(","));
                }}
              />
            </Grid>
          </CreateBillingProfilePageLayout>
        );
      }}
    </Formik>
  );
}
